import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../components/AuthProvider';
import { apiUrl } from '../../../lib/api';

const MONO = "'JetBrains Mono', monospace";
const ORBITRON = "'Orbitron', monospace";
const TEAL = '#00FFE5';
const TEAL_BORDER = 'rgba(0,255,229,0.2)';
const TEAL_FAINT = 'rgba(0,255,229,0.06)';
const BG = '#080810';
const MUTED = 'rgba(0,255,229,0.45)';
const WHITE = '#E8E8F0';
const GREEN = '#2DC896';

const TOTAL_STEPS = 4;

const BUSINESS_TYPES = [
  'Agency',
  'Service business',
  'Coach / consultant',
  'Creator / personal brand',
  'Ecommerce',
  'Local business',
  'Other',
];

const MOMENTUM_BLOCKERS = [
  'Not enough leads',
  'Leads not replying',
  'Booked calls not closing',
  'Too much manual follow-up',
  'Delivery is messy',
  'Content is inconsistent',
  'Old leads are sitting idle',
];

const PRIMARY_GOALS = [
  'Generate and organize leads',
  'Follow up and reactivate prospects',
  'Reduce no-shows and improve appointment handling',
  'Build content systems',
  'Improve delivery operations',
  'Show pipeline and conversion visibility',
];

// Maps primary goal to crew param for future dynamic routing
const GOAL_TO_CREW = {
  'Generate and organize leads': 'sales',
  'Follow up and reactivate prospects': 'sales',
  'Reduce no-shows and improve appointment handling': 'sales',
  'Build content systems': 'content',
  'Improve delivery operations': 'delivery',
  'Show pipeline and conversion visibility': 'sales',
};

// Maps momentum blocker to recommended_crew
const BLOCKER_TO_CREW = {
  'Not enough leads': 'sales',
  'Leads not replying': 'sales',
  'Booked calls not closing': 'sales',
  'Too much manual follow-up': 'sales',
  'Old leads are sitting idle': 'sales',
  'Delivery is messy': 'delivery',
  'Content is inconsistent': 'content',
  'Show pipeline and conversion visibility': 'operations',
};

// Maps primary goal to first_operation
const GOAL_TO_OPERATION = {
  'Generate and organize leads': 'lead_generation',
  'Follow up and reactivate prospects': 'follow_up',
  'Reduce no-shows and improve appointment handling': 'appointment_handling',
  'Build content systems': 'content_system',
  'Improve delivery operations': 'delivery_ops',
  'Show pipeline and conversion visibility': 'pipeline_visibility',
};

const SelectOption = ({ label, selected, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%',
      background: selected ? 'rgba(0,255,229,0.08)' : 'rgba(255,255,255,0.02)',
      border: `1px solid ${selected ? TEAL : 'rgba(255,255,255,0.08)'}`,
      borderRadius: 4,
      padding: '12px 16px',
      textAlign: 'left',
      fontFamily: MONO,
      fontSize: '0.78rem',
      color: selected ? TEAL : 'rgba(232,232,240,0.7)',
      cursor: 'pointer',
      letterSpacing: '0.04em',
      transition: 'all 0.15s ease',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}
  >
    <span style={{
      width: 14,
      height: 14,
      borderRadius: 2,
      border: `1px solid ${selected ? TEAL : 'rgba(255,255,255,0.2)'}`,
      background: selected ? 'rgba(0,255,229,0.2)' : 'transparent',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '0.6rem',
      color: TEAL,
    }}>
      {selected ? '✓' : ''}
    </span>
    {label}
  </button>
);

const TextInput = ({ label, value, onChange, placeholder }) => (
  <div style={{ marginBottom: 16 }}>
    <label style={{ fontFamily: MONO, fontSize: '0.65rem', color: MUTED, letterSpacing: '0.12em', display: 'block', marginBottom: 6 }}>
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{
        width: '100%',
        background: 'rgba(255,255,255,0.03)',
        border: `1px solid ${TEAL_BORDER}`,
        borderRadius: 4,
        padding: '11px 14px',
        color: WHITE,
        fontSize: '0.82rem',
        fontFamily: MONO,
        outline: 'none',
        boxSizing: 'border-box',
        letterSpacing: '0.02em',
      }}
    />
  </div>
);

const OnboardingModal = ({ onComplete }) => {
  const navigate = useNavigate();
  const { session, refreshProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [businessType, setBusinessType] = useState('');
  const [momentumBlocker, setMomentumBlocker] = useState('');
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [mainOffer, setMainOffer] = useState('');
  const [idealCustomer, setIdealCustomer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const canProceed = () => {
    if (step === 1) return !!businessType;
    if (step === 2) return !!momentumBlocker;
    if (step === 3) return !!primaryGoal;
    if (step === 4) return true; // Step 4 is optional
    return false;
  };

  const handleLaunch = async () => {
    setSubmitting(true);
    setError('');

    // Derive normalized fields
    const recommended_crew = BLOCKER_TO_CREW?.[momentumBlocker] || 'sales';
    const first_operation = GOAL_TO_OPERATION?.[primaryGoal] || 'lead_generation';

    // Build onboarding context — easy to extend for dynamic crew routing
    const onboardingContext = {
      business_type: businessType,
      momentum_blocker: momentumBlocker,
      primary_goal: primaryGoal,
      crew_hint: GOAL_TO_CREW?.[primaryGoal] || 'sales', // future: dynamic crew selection
      business_name: businessName?.trim() || null,
      main_offer: mainOffer?.trim() || null,
      ideal_customer: idealCustomer?.trim() || null,
      completed_at: new Date()?.toISOString(),
    };

    try {
      const token = session?.access_token;
      if (!token) throw new Error('Authentication session is unavailable');
      const response = await fetch(apiUrl('/api/profile/onboarding'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          onboarding_context: onboardingContext,
          recommended_crew,
          first_operation,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        console.error('Onboarding save error:', body?.error || response.status);
        setError('Failed to save. Please try again.');
        setSubmitting(false);
        return;
      }

      await refreshProfile();

      if (onComplete) onComplete();
      navigate('/command-center-ai-execution-interface', { replace: true });
    } catch (err) {
      console.error('Onboarding error:', err);
      setError('Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  const stepLabels = [
    'BUSINESS TYPE',
    'MOMENTUM BLOCKER',
    'PRIMARY GOAL',
    'BUSINESS DETAILS',
  ];

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: BG,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      overflowY: 'auto',
      padding: '40px 16px 60px',
    }}>
      {/* Header */}
      <div style={{ width: '100%', maxWidth: 560, marginBottom: 40 }}>
        <div style={{ fontFamily: ORBITRON, fontSize: '0.7rem', color: TEAL, letterSpacing: '0.2em', marginBottom: 8 }}>
          RECALLCORE™ — OPERATOR SETUP
        </div>
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {Array.from({ length: TOTAL_STEPS })?.map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 2,
                borderRadius: 2,
                background: i < step ? TEAL : 'rgba(0,255,229,0.12)',
                transition: 'background 0.3s ease',
              }}
            />
          ))}
        </div>
        <div style={{ fontFamily: MONO, fontSize: '0.6rem', color: MUTED, letterSpacing: '0.1em' }}>
          STEP {step} OF {TOTAL_STEPS} — {stepLabels?.[step - 1]}
        </div>
      </div>
      {/* Step Content */}
      <div style={{ width: '100%', maxWidth: 560 }}>

        {/* STEP 1 */}
        {step === 1 && (
          <div>
            <h2 style={{ fontFamily: ORBITRON, fontSize: '1.3rem', fontWeight: 700, color: WHITE, letterSpacing: '0.06em', marginBottom: 8 }}>
              What best describes your business?
            </h2>
            <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, letterSpacing: '0.06em', marginBottom: 28 }}>
              This helps RecallCore route the right crew to your operations.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {BUSINESS_TYPES?.map(opt => (
                <SelectOption
                  key={opt}
                  label={opt}
                  selected={businessType === opt}
                  onClick={() => setBusinessType(opt)}
                />
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div>
            <h2 style={{ fontFamily: ORBITRON, fontSize: '1.3rem', fontWeight: 700, color: WHITE, letterSpacing: '0.06em', marginBottom: 8 }}>
              Where are you losing momentum right now?
            </h2>
            <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, letterSpacing: '0.06em', marginBottom: 28 }}>
              Select the biggest friction point in your current operations.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {MOMENTUM_BLOCKERS?.map(opt => (
                <SelectOption
                  key={opt}
                  label={opt}
                  selected={momentumBlocker === opt}
                  onClick={() => setMomentumBlocker(opt)}
                />
              ))}
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div>
            <h2 style={{ fontFamily: ORBITRON, fontSize: '1.3rem', fontWeight: 700, color: WHITE, letterSpacing: '0.06em', marginBottom: 8 }}>
              What do you want RecallCore to help you fix first?
            </h2>
            <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, letterSpacing: '0.06em', marginBottom: 28 }}>
              Your primary goal determines which crew activates first.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {PRIMARY_GOALS?.map(opt => (
                <SelectOption
                  key={opt}
                  label={opt}
                  selected={primaryGoal === opt}
                  onClick={() => setPrimaryGoal(opt)}
                />
              ))}
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div>
            <h2 style={{ fontFamily: ORBITRON, fontSize: '1.3rem', fontWeight: 700, color: WHITE, letterSpacing: '0.06em', marginBottom: 8 }}>
              Tell us about your business
            </h2>
            <p style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, letterSpacing: '0.06em', marginBottom: 4 }}>
              This context powers every crew operation going forward.
            </p>
            <p style={{ fontFamily: MONO, fontSize: '0.62rem', color: 'rgba(0,255,229,0.3)', letterSpacing: '0.08em', marginBottom: 28 }}>
              OPTIONAL — you can skip this and launch now.
            </p>
            <TextInput
              label="BUSINESS NAME"
              value={businessName}
              onChange={e => setBusinessName(e?.target?.value)}
              placeholder="e.g. Apex Growth Agency"
            />
            <TextInput
              label="MAIN OFFER"
              value={mainOffer}
              onChange={e => setMainOffer(e?.target?.value)}
              placeholder="e.g. Done-for-you lead generation for coaches"
            />
            <TextInput
              label="IDEAL CUSTOMER"
              value={idealCustomer}
              onChange={e => setIdealCustomer(e?.target?.value)}
              placeholder="e.g. Online coaches doing $10K–$50K/month"
            />
            {error && (
              <div style={{ fontFamily: MONO, fontSize: '0.65rem', color: '#FF4444', letterSpacing: '0.06em', marginBottom: 16 }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 36 }}>
          {step > 1 ? (
            <button
              onClick={() => setStep(s => s - 1)}
              style={{
                background: 'transparent',
                border: 'none',
                fontFamily: MONO,
                fontSize: '0.65rem',
                color: MUTED,
                letterSpacing: '0.1em',
                cursor: 'pointer',
                padding: '8px 0',
              }}
            >
              ← BACK
            </button>
          ) : (
            <div />
          )}

          {step < TOTAL_STEPS ? (
            <button
              onClick={() => canProceed() && setStep(s => s + 1)}
              disabled={!canProceed()}
              style={{
                background: canProceed() ? 'rgba(0,255,229,0.08)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${canProceed() ? TEAL : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 3,
                padding: '11px 28px',
                fontFamily: MONO,
                fontSize: '0.72rem',
                color: canProceed() ? TEAL : 'rgba(255,255,255,0.2)',
                letterSpacing: '0.12em',
                cursor: canProceed() ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s ease',
              }}
            >
              CONTINUE →
            </button>
          ) : (
            <button
              onClick={handleLaunch}
              disabled={submitting}
              style={{
                background: !submitting ? 'rgba(0,255,229,0.1)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${!submitting ? TEAL : 'rgba(255,255,255,0.08)'}`,
                borderRadius: 3,
                padding: '13px 32px',
                fontFamily: ORBITRON,
                fontSize: '0.8rem',
                fontWeight: 700,
                color: !submitting ? TEAL : 'rgba(255,255,255,0.2)',
                letterSpacing: '0.15em',
                cursor: !submitting ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s ease',
              }}
            >
              {submitting ? 'INITIALIZING...' : 'LAUNCH RECALLCORE'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
